"use client";

import { TechStackProps } from "@/types/component.types";
import SectionWrapper from "@/components/ui/SectionWrapper";
import Headline from "@/components/ui/Headline";
import Text from "@/components/ui/Text";
import Image from "next/image";
import { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import { motion } from "framer-motion";

export default function TechStack({ blok }: { blok: TechStackProps }) {
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const isDragging = useRef(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setActiveTooltip(null);
      }
    };

    const handleScroll = () => {
      setActiveTooltip(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, { passive: true });
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleItemClick = (uid: string) => {
    setActiveTooltip(prev => (prev === uid ? null : uid));
  };

  return (
    <SectionWrapper
      className="container flex flex-col items-center gap-12 overflow-x-clip"
      spaces={blok.spaces}
      id="techstack"
    >
      <div className="w-full flex flex-col max-w-[810px] mx-auto">
        {blok.headline?.[0] && (
          <Headline
            text={blok.headline[0].text}
            level={blok.headline[0].level}
            className="mb-4"
          />
        )}

        <Text variant="body" className="mb-10">
          {blok.text}
        </Text>

        <div ref={containerRef} className="flex flex-wrap gap-6 md:pr-10">
          {blok.tech_items?.map((item) => {
            const isActive = activeTooltip === item._uid;

            return (
              <button 
                className="group relative flex items-center justify-center shrink-0 cursor-pointer text-left rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green p-4"
                type="button"
                key={item._uid} 
                onClick={(e) => {
                  if (isDragging.current) {
                    e.preventDefault();
                    return;
                  }
                  if (item._uid) handleItemClick(item._uid);
                }}
                aria-label={`Show details for ${item.name}`}
                aria-expanded={isActive}
              >
                <motion.div 
                  className="custom-cursor-hover xl:hover:cursor-none z-10 transform-gpu"
                  drag
                  dragConstraints={{ top: 0, left: 0, right: 0, bottom: 0 }}
                  dragElastic={0.8}
                  dragTransition={{ bounceStiffness: 600, bounceDamping: 15 }}
                  whileDrag={{ scale: 1.2, zIndex: 50, cursor: "grabbing" }}
                  whileHover={{ scale: 1.1 }}
                  onDragStart={() => {
                    isDragging.current = true;
                    setActiveTooltip(null);
                  }}
                  onDragEnd={() => {
                    setTimeout(() => {
                      isDragging.current = false;
                    }, 100);
                  }}
                >
                  <Image
                    src={item.icon[0].src.filename}
                    alt={item.icon[0].alt} 
                    width={64}
                    height={64}
                    draggable={false}
                    className={classNames(
                      "size-14 md:size-18 xl:size-20 object-contain duration-300 transform-gpu",
                      "drop-shadow-[0_0_6px_rgba(23,23,23,0.4)]",
                      "dark:drop-shadow-[0_0_8px_rgba(252,244,225,0.5)] md:dark:drop-shadow-[0_0_8px_rgba(252,244,225,0.3)]",
                      {
                        "drop-shadow-[0_0_10px_rgba(23,23,23,0.4)] dark:drop-shadow-[0_0_10px_rgba(252,244,225,0.2)]": isActive,
                        "xl:group-hover:drop-shadow-[0_0_10px_rgba(23,23,23,0.4)] dark:xl:group-hover:drop-shadow-[0_0_10px_rgba(252,244,225,0.2)]": !isActive
                      }
                    )}
                    loading="lazy"
                  />
                </motion.div>
                
                {/* tooltip */}
                <div 
                  className={classNames(
                    "z-50 flex justify-center",
                    "fixed inset-x-4 bottom-4 md:bottom-8",
                    "xl:absolute xl:inset-auto xl:bottom-full xl:left-1/2 xl:-translate-x-1/2 xl:mb-2",
                    {
                      "pointer-events-auto": isActive,
                      "pointer-events-none": !isActive
                    }
                  )}
                  aria-hidden={!isActive}
                >
                  <div 
                    className={classNames(
                      "relative bg-white/90 dark:bg-black/80 backdrop-blur-md rounded-xl p-4 shadow-2xl border border-black/5 dark:border-white/10 text-center transition-all duration-300 transform",
                      "w-full max-w-[400px] md:max-w-[500px] xl:w-max xl:max-w-[320px]",
                      {
                        "opacity-100 translate-y-0": isActive,
                        "opacity-0 translate-y-4 xl:group-hover:opacity-100 xl:group-hover:translate-y-0": !isActive
                      }
                    )}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Text variant="small">
                      {item.name}
                    </Text>

                    {item.description && (
                      <Text variant="detail" className="mt-1 opacity-80">
                        {item.description}
                      </Text>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </SectionWrapper>
  );
}